<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    AURAS,
    CHAT_CHANNELS,
    CLASSES,
    HUB_NPCS,
    MAX_PARTY,
    bracketSize,
    type Bracket,
  } from '@arena/shared';
  import { challenge, click } from '../game/Sfx';
  import { hud } from '../game/hud.svelte';
  import { app, send } from '../lib/net.svelte';
  import { settings } from '../lib/settings.svelte';
  import Chat from './Chat.svelte';
  import { STEPS } from '../lib/tutorial';
  import ClassGrid from './ClassGrid.svelte';

  let now = $state(Date.now());
  let chat: Chat | undefined = $state();
  /** The player menu, opened from a frame or a name in chat. */
  let menu = $state<{ name: string; x: number; y: number } | null>(null);
  let menuEl: HTMLDivElement | undefined = $state();
  let maps = $state<string[]>([]);
  let practiceMap = $state('');
  let privateMap = $state('');

  onMount(() => {
    fetch('/api/maps')
      .then((r) => r.json())
      .then((m: string[]) => {
        maps = m;
        practiceMap = m[0] ?? '';
      });
    const timer = setInterval(() => (now = Date.now()), 500);
    return () => clearInterval(timer);
  });

  const partySize = $derived(app.party?.members.length ?? 1);
  const isLeader = $derived(!app.party || app.party.leaderId === app.user?.id);
  const waited = $derived(
    app.queue.since
      ? Math.max(0, Math.floor((now - app.queue.since) / 1000))
      : 0,
  );
  const npc = $derived(HUB_NPCS.find((n) => n.id === hud.npc));

  function queue(bracket: Bracket) {
    send({ t: 'queue', bracket });
    // Asked on the click that queues, so a popped queue can notify a hidden tab.
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    )
      void Notification.requestPermission();
  }

  function invite(name: string) {
    if (name.trim()) send({ t: 'partyInvite', name: name.trim() });
  }

  function answerInvite(accept: boolean) {
    send({ t: accept ? 'partyAccept' : 'partyDecline' });
    app.invite = null;
  }

  function answerDuel(accept: boolean) {
    send({ t: accept ? 'duelAccept' : 'duelDecline' });
    app.duel = null;
  }

  $effect(() => {
    if (npc) untrack(click);
  });

  $effect(() => {
    if (app.duel) untrack(challenge);
  });

  export function openMenu(name: string, event: MouseEvent) {
    click();
    menu = { name, x: event.clientX, y: event.clientY };
  }

  /** The target frame's menu: players other than us only. */
  export function targetMenu(event: MouseEvent) {
    const target = hud.target;
    if (
      target &&
      !HUB_NPCS.some((n) => n.id === target.id) &&
      target.id !== hud.player?.id
    )
      openMenu(target.name, event);
  }

  function closeMenuOutside(event: PointerEvent) {
    if (menu && !menuEl?.contains(event.target as Node)) menu = null;
  }
</script>

<svelte:window
  onpointerdown={closeMenuOutside}
  onkeydowncapture={(e) => {
    if (e.key !== 'Escape' || !menu) return;
    menu = null;
    click();
    e.stopPropagation();
  }}
/>

<div class="lobby">
  <div class="stats">
    {app.online} online · {Object.values(app.queued).reduce((a, b) => a + b, 0)}
    in queue · {app.fighting} in arenas ·
    {#if app.status !== 'online'}<span class="status {app.status}"
        >{app.status}</span
      >{:else}{app.ping ?? '–'} ms{/if}{#if settings.showFps}{` · ${hud.fps} fps`}{/if}
  </div>

  {#if app.queue.bracket}
    <div class="panel queued">
      Searching for a {app.queue.bracket} match… {waited}s
      {#if isLeader}<button onclick={() => send({ t: 'unqueue' })}
          >Leave queue</button
        >{/if}
    </div>
  {/if}

  <div class="prompts">
    {#if app.invite}
      <div class="panel prompt">
        <span><strong>{app.invite}</strong> invites you to join a party.</span>
        <button onclick={() => answerInvite(true)}>Accept</button>
        <button onclick={() => answerInvite(false)}>Decline</button>
      </div>
    {/if}
  </div>

  {#if app.duel}
    <div class="panel duel" role="alertdialog" aria-labelledby="duel-title">
      <h2 id="duel-title">{app.duel.team ? 'Team duel' : 'Duel'}</h2>
      <p>
        <strong>{app.duel.from}</strong>
        {app.duel.team
          ? 'challenges your party to a team duel.'
          : 'challenges you to a duel.'}
      </p>
      <div class="duel-buttons">
        <button class="accept" onclick={() => answerDuel(true)}>Accept</button>
        <button onclick={() => answerDuel(false)}>Decline</button>
      </div>
    </div>
  {/if}

  <div class="side">
    {#if settings.tutorial !== 'done'}
      <section
        class="panel tutorial"
        aria-label="Getting started"
        aria-live="polite"
      >
        <h3>Getting started</h3>
        <p>{STEPS[settings.tutorial].text}</p>
        <button class="skip" onclick={() => (settings.tutorial = 'done')}
          >Skip tutorial</button
        >
      </section>
    {/if}
    {#if app.party}
      <section class="panel party-panel">
        <h3>Party</h3>
        <ul>
          {#each app.party.members as member (member.id)}
            <li>
              <span style="color: {CLASSES[member.classes[0]].color}"
                >{member.name}</span
              >
              <span class="muted small"
                >{member.classes.map((c) => CLASSES[c].name).join(' / ')}</span
              >
              {#if member.id === app.party.leaderId}<span class="badge"
                  >Leader</span
                >{/if}
              {#if member.practice}<span
                  class="badge"
                  title="Practicing in {member.practice}">Practicing</span
                >{/if}
            </li>
          {/each}
        </ul>
        <h4>Private match</h4>
        <div class="sides">
          {#each [0, 1] as team (team)}
            <ul>
              <li class="muted small">Side {team + 1}</li>
              {#each app.party.members.filter((m) => m.team === team) as member (member.id)}
                <li>
                  <span style="color: {CLASSES[member.classes[0]].color}"
                    >{member.name}</span
                  >
                  {#if isLeader}<button
                      class="swap"
                      title="Move to the other side"
                      aria-label="Move {member.name} to the other side"
                      onclick={() =>
                        send({ t: 'partySwapTeam', memberId: member.id })}
                      >⇄</button
                    >{/if}
                </li>
              {/each}
            </ul>
          {/each}
        </div>
        {#if isLeader}
          <div class="row">
            <select bind:value={privateMap} aria-label="Private match arena">
              <option value="">Random arena</option>
              {#each maps as file (file)}
                <option value={file}>{file}</option>
              {/each}
            </select>
            <button
              onclick={() =>
                send({
                  t: 'queue',
                  bracket: 'private',
                  map: privateMap || undefined,
                })}>Start</button
            >
          </div>
        {:else}
          <p class="muted small">
            Your party leader arranges sides and starts.
          </p>
        {/if}
        <button onclick={() => send({ t: 'partyLeave' })}>Leave party</button>
      </section>
    {/if}
  </div>

  {#if npc}
    <section class="panel dialog">
      <header>
        <div>
          <h2>{npc.name}</h2>
          <span class="muted">&lt;{npc.title}&gt;</span>
        </div>
        <button
          class="close"
          onclick={() => (hud.npc = null)}
          aria-label="Close">×</button
        >
      </header>
      {#if npc.role === 'class'}
        <p>Every fighter needs a calling. Two, in this arena.</p>
        <ClassGrid compact />
        <div class="row spells">
          <span class="muted">
            {#each app.classes as id (id)}
              {@const innate = AURAS[CLASSES[id].innate]}
              <span class="calling"
                ><strong style="color: {CLASSES[id].color}"
                  >{CLASSES[id].name}</strong
                >
                {CLASSES[id].description}
                {innate.name}: {innate.description}</span
              >
            {/each}
          </span>
          <button
            class:quest={settings.tutorial === 'spells'}
            onclick={() => ((hud.npc = null), (hud.panel = 'spellbook'))}
            >Show me my spells</button
          >
        </div>
      {:else if npc.role === 'practice'}
        {@const practicing = app.party?.members.filter((m) => m.practice) ?? []}
        <p>
          Pick an arena and I'll set up training dummies around it. They get
          back up every time.
        </p>
        {#each practicing as member (member.id)}
          <div class="row">
            <span
              ><strong style="color: {CLASSES[member.classes[0]].color}"
                >{member.name}</strong
              >
              is practicing in {member.practice}.</span
            >
            <button
              onclick={() => send({ t: 'joinPractice', memberId: member.id })}
              >Join {member.name}</button
            >
          </div>
        {/each}
        <div class="row">
          <select bind:value={practiceMap} aria-label="Practice arena">
            {#each maps as file (file)}
              <option value={file}>{file}</option>
            {/each}
          </select>
          <button
            onclick={() =>
              send({
                t: 'queue',
                bracket: 'practice',
                map: practiceMap || undefined,
              })}
            >{practicing.length ? 'Start your own' : 'Start practice'}</button
          >
        </div>
        {#if app.party && !practicing.length}<p class="muted small">
            Your party can join you here once you've started.
          </p>{/if}
      {:else}
        {@const bracket = npc.role}
        <p>
          {bracket === '1v1'
            ? 'Just you and a rival. No healer to blame.'
            : `Teams of ${bracketSize(bracket)} fight to the last one standing. Bring a party or I'll find you partners.`}
        </p>
        <p class="muted small">{app.queued[bracket]} players in queue</p>
        {#if app.queue.bracket === bracket}
          <p class="gold">You're in this queue… {waited}s</p>
        {:else}
          <button
            onclick={() => queue(bracket)}
            disabled={!isLeader || partySize > bracketSize(bracket)}
            >Join {bracket} queue</button
          >
        {/if}
      {/if}
      {#if !isLeader && npc.role !== 'class' && npc.role !== 'practice'}<p
          class="muted small"
        >
          Your party leader queues for the group.
        </p>{/if}
    </section>
  {/if}

  <Chat
    bind:this={chat}
    channels={app.party
      ? CHAT_CHANNELS
      : CHAT_CHANNELS.filter((c) => c !== 'party')}
    oninvite={(name) => invite(name || hud.target?.name || '')}
    hub
    onname={openMenu}
  />

  {#if menu}
    {@const { name } = menu}
    <div
      class="panel menu"
      bind:this={menuEl}
      style:left="{menu.x / settings.uiScale}px"
      style:top="{menu.y / settings.uiScale}px"
      style:transform={menu.y > innerHeight / 2 ? 'translateY(-100%)' : null}
    >
      <strong>{name}</strong>
      <button onclick={() => (chat?.whisper(name), (menu = null))}
        >Whisper</button
      >
      {#if isLeader && partySize < MAX_PARTY && !app.party?.members.some((m) => m.name === name)}
        <button onclick={() => (invite(name), (menu = null))}>Invite</button>
      {/if}
      <button onclick={() => (send({ t: 'duel', name }), (menu = null))}
        >Duel</button
      >
      <button onclick={() => (menu = null)}>Cancel</button>
    </div>
  {/if}

  <div class="hint">
    Right-click an NPC to talk · WASD to move · Right drag to steer
  </div>

  {#if app.error}<div class="toast">{app.error}</div>{/if}
</div>

<style>
  .lobby {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  .lobby > :global(*) {
    pointer-events: auto;
  }
  .lobby > .hint,
  .lobby > .stats,
  .lobby > .prompts {
    pointer-events: none;
  }
  .stats {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 14px;
    white-space: nowrap;
    text-shadow: 0 1px 3px black;
  }
  .menu {
    position: absolute;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 140px;
    padding: 8px;
    background: var(--panel-solid);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
  }
  .menu strong {
    color: var(--gold);
    padding: 0 4px 4px;
  }
  .side {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 240px;
  }
  .tutorial {
    border-color: var(--gold);
    background: var(--panel-solid);
  }
  .tutorial h3 {
    margin: 0 0 6px;
    color: var(--gold);
  }
  .tutorial p {
    margin: 0 0 8px;
  }
  .skip {
    padding: 2px 8px;
    font-size: 12px;
  }
  .spells {
    justify-content: flex-end;
    margin: 8px 0 0;
  }
  .spells > span {
    flex: 1;
  }
  .calling {
    display: block;
  }
  .quest {
    border-color: var(--gold);
    color: var(--gold);
    box-shadow: 0 0 10px rgba(230, 192, 106, 0.6);
  }
  .queued {
    position: absolute;
    top: 38px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 12px;
    align-items: center;
    color: var(--gold);
    border-color: var(--gold);
  }
  /* Below the player frame's default spot and above the chat (its top is 332px up); party frames sit behind. */
  .dialog {
    position: absolute;
    top: 150px;
    left: 12px;
    width: min(520px, calc(100vw - 24px));
    height: min(460px, calc(100% - 494px));
    overflow-y: auto;
    border-color: var(--gold);
    background: var(--panel-solid);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
  }
  .dialog header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
  }
  .dialog h2 {
    margin: 0;
  }
  .close {
    padding: 0 8px;
    font-size: 18px;
  }
  .prompts {
    position: absolute;
    top: 90px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .prompt {
    display: flex;
    gap: 12px;
    align-items: center;
    border-color: var(--gold);
    background: var(--panel-solid);
    pointer-events: auto;
  }
  .duel {
    position: absolute;
    top: 38%;
    left: 50%;
    z-index: 1;
    width: min(360px, calc(100vw - 24px));
    padding: 20px 24px;
    text-align: center;
    border-color: var(--gold);
    background: var(--panel-solid);
    box-shadow:
      0 0 32px color-mix(in srgb, var(--gold) 35%, transparent),
      0 10px 40px rgba(0, 0, 0, 0.6);
    transform: translate(-50%, -50%);
    animation: duel-in 0.3s ease-out;
  }
  .duel h2 {
    font-size: 26px;
    letter-spacing: 3px;
    text-transform: uppercase;
  }
  .duel p {
    margin: 0 0 16px;
    font-size: 16px;
  }
  .duel-buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
  }
  .duel-buttons button {
    min-width: 110px;
    padding: 8px 16px;
    font-size: 16px;
  }
  .accept {
    font-weight: bold;
  }
  @keyframes duel-in {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1.15);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .duel {
      animation: none;
    }
  }
  .sides {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .sides ul {
    margin: 0 0 8px;
    padding: 0;
    list-style: none;
  }
  .swap {
    padding: 0 4px;
    font-size: 12px;
  }
  /* Clears the action bars, the other class's row and the combo points above them. */
  .hint {
    position: absolute;
    bottom: 182px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 13px;
    color: var(--text);
    text-shadow: 0 1px 3px black;
  }
  .status {
    text-transform: uppercase;
  }
  .status.online {
    color: var(--friendly);
  }
  .status.offline {
    color: var(--enemy);
  }
  .muted {
    color: var(--muted);
  }
  .gold {
    color: var(--gold);
  }
  .small {
    font-size: 12px;
  }
  .row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }
  select {
    text-transform: capitalize;
  }
  .badge {
    font-size: 11px;
    border: 1px solid var(--gold);
    color: var(--gold);
    border-radius: 3px;
    padding: 1px 5px;
  }
  ul {
    padding-left: 18px;
  }
  .toast {
    position: absolute;
    bottom: 146px;
    left: 50%;
    transform: translateX(-50%);
    background: #4a1410;
    border: 1px solid var(--enemy);
    padding: 8px 16px;
    border-radius: 4px;
  }
</style>
